// PaymentReviewPage.jsx
import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { formatDate } from "../../utils/dateFormatter";
import { AuthContext } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import PHIWarningBanner from "../compliance/PHIWarningBanner";
import PurposeOfUseBanner from "../compliance/PurposeOfUseBanner";
import DataHandlingNotice from "../compliance/DataHandlingNotice";

const PaymentReviewPage = () => {
  const [mileageLogs, setMileageLogs] = useState([]);
  const [jobLogs, setJobLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'mileage', 'joblogs'
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    is_paid: "",
    is_billable: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]); // { type: 'mileage'|'joblog', id: string }

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [pendingPayment, setPendingPayment] = useState(null);

  const { user, loadingUser } = useContext(AuthContext);
  const is_admin = user?.is_admin;
  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (loadingUser || !is_admin || !tenant_id) return;
    fetchAllData();
  }, [is_admin, tenant_id, loadingUser, filters.is_paid, filters.is_billable]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setSelectedItems([]);

      const queryParams = {
        is_paid: filters.is_paid === "" ? undefined : filters.is_paid,
        is_billable:
          filters.is_billable === "" ? undefined : filters.is_billable,
      };

      Object.keys(queryParams).forEach((key) => {
        if (!queryParams[key]) delete queryParams[key];
      });

      // Fetch both mileage and job logs in parallel
      const [mileageRes, jobLogRes] = await Promise.all([
        secureAxios.get("/api/mileage", { params: queryParams }),
        secureAxios.get("/api/job-logs/review", { params: queryParams }),
      ]);

      setMileageLogs(mileageRes.data);
      setJobLogs(jobLogRes.data);
    } catch (err) {
      console.error("Error loading data");
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  // Normalize data into a unified format
  const getNormalizedData = () => {
    const normalizedMileage = mileageLogs.map((log) => ({
      id: log.log_id,
      type: "mileage",
      date: log.trip_date,
      consumer_name: log.consumer_name,
      user_name: log.user_name,
      authorization_number: log.authorization_number,
      service_name: "Mileage",
      is_billable: log.is_billable,
      quantity: log.total_miles,
      quantity_label: "miles",
      rate: log.rate,
      amount_to_pay: log.amount_to_pay,
      is_paid: log.is_paid,
      paid_at: log.paid_at,
      previously_paid_at: log.previously_paid_at,
      amount_paid: log.amount_paid,
      original: log,
    }));

    const normalizedJobLogs = jobLogs.map((log) => ({
      id: log.job_log_id,
      type: "joblog",
      date: log.date_of_service,
      consumer_name: log.consumer_name,
      user_name: log.user_name,
      authorization_number: log.authorization_number,
      service_name: log.service_name || "Job Coaching",
      is_billable: log.is_billable,
      quantity: log.hours_worked,
      quantity_label: "hrs",
      rate: log.hourly_rate,
      amount_to_pay: log.amount_to_pay,
      is_paid: log.is_paid,
      paid_at: log.paid_at,
      previously_paid_at: log.previously_paid_at,
      amount_paid: log.amount_paid,
      original: log,
    }));

    return [...normalizedMileage, ...normalizedJobLogs];
  };

  const getFilteredData = () => {
    let data = getNormalizedData();

    // Filter by tab
    if (activeTab === "mileage") {
      data = data.filter((item) => item.type === "mileage");
    } else if (activeTab === "joblogs") {
      data = data.filter((item) => item.type === "joblog");
    }

    // Date filtering
    if (filters.start_date) {
      const startDate = new Date(filters.start_date);
      data = data.filter((item) => new Date(item.date) >= startDate);
    }

    if (filters.end_date) {
      const endDate = new Date(filters.end_date);
      data = data.filter((item) => new Date(item.date) <= endDate);
    }

    // Search term filtering
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      data = data.filter(
        (item) =>
          (item.consumer_name || "").toLowerCase().includes(search) ||
          (item.user_name || "").toLowerCase().includes(search)
      );
    }

    // Sort by date descending
    data.sort((a, b) => new Date(b.date) - new Date(a.date));

    return data;
  };

  const filteredData = getFilteredData();

  // Calculate totals
  const totalAmount = filteredData.reduce(
    (sum, item) => sum + Number(item.amount_to_pay || 0),
    0
  );

  const unpaidTotal = filteredData
    .filter((item) => !item.is_paid)
    .reduce((sum, item) => sum + Number(item.amount_to_pay || 0), 0);

  const selectedTotal = selectedItems.reduce((sum, selected) => {
    const item = filteredData.find(
      (d) => d.id === selected.id && d.type === selected.type
    );
    return sum + Number(item?.amount_to_pay || 0);
  }, 0);

  // Selection handlers
  const handleSelectItem = (type, id) => {
    setSelectedItems((prev) => {
      const exists = prev.find((s) => s.type === type && s.id === id);
      if (exists) {
        return prev.filter((s) => !(s.type === type && s.id === id));
      } else {
        return [...prev, { type, id }];
      }
    });
  };

  const handleSelectAll = () => {
    const unpaidItems = filteredData
      .filter((item) => !item.is_paid)
      .map((item) => ({ type: item.type, id: item.id }));

    if (selectedItems.length === unpaidItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(unpaidItems);
    }
  };

  const isSelected = (type, id) => {
    return selectedItems.some((s) => s.type === type && s.id === id);
  };

  // Payment handlers
  const markAsPaid = (type, id) => {
    const item = filteredData.find((d) => d.id === id && d.type === type);
    setPendingPayment({
      type: "single",
      recordType: type,
      id: id,
      amount: Number(item?.amount_to_pay || 0),
    });
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setShowPaymentModal(true);
  };

  const markAsUnpaid = async (type, id) => {
    const item = filteredData.find((d) => d.id === id && d.type === type);
    const amountPaid = Number(item?.amount_paid || item?.amount_to_pay || 0);

    if (
      !window.confirm(
        `Mark this ${
          type === "mileage" ? "mileage" : "job log"
        } as UNPAID?\n\n` +
          `Amount: $${amountPaid.toFixed(2)}\n` +
          `Date Paid: ${formatDate(item?.paid_at)}\n\n` +
          `By confirming, you acknowledge that:\n` +
          `- This action will be logged for audit purposes\n` +
          `- You are reversing a previous payment record\n` +
          `- You are authorized to process this reversal`
      )
    ) {
      return;
    }

    try {
      if (type === "mileage") {
        await secureAxios.post(`/api/mileage/mark-unpaid/${id}`);
      } else {
        await secureAxios.patch(`/api/job-logs/${id}/mark-unpaid`);
      }
      toast.success(
        `${type === "mileage" ? "Mileage" : "Job log"} marked as unpaid`
      );
      fetchAllData();
    } catch (err) {
      console.error("Error marking as unpaid");
      toast.error("Failed to mark as unpaid");
    }
  };

  const markSelectedAsPaid = () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item to mark as paid");
      return;
    }

    const totalAmount = selectedItems.reduce((sum, selected) => {
      const item = filteredData.find(
        (d) => d.id === selected.id && d.type === selected.type
      );
      return sum + Number(item?.amount_to_pay || 0);
    }, 0);

    setPendingPayment({
      type: "bulk",
      items: selectedItems,
      amount: totalAmount,
      count: selectedItems.length,
    });
    setPaymentDate(new Date().toISOString().split("T")[0]);
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    if (!pendingPayment) return;

    try {
      if (pendingPayment.type === "single") {
        if (pendingPayment.recordType === "mileage") {
          await secureAxios.post(
            `/api/mileage/mark-paid/${pendingPayment.id}`,
            {
              amount_paid: pendingPayment.amount,
              paid_at: paymentDate,
            }
          );
        } else {
          await secureAxios.patch(
            `/api/job-logs/${pendingPayment.id}/mark-paid`,
            {
              paid_by: user?.user_id,
              amount_paid: pendingPayment.amount,
              paid_at: paymentDate,
              purpose_of_use: "payment",
            }
          );
        }
        toast.success("Item marked as paid");
      } else if (pendingPayment.type === "bulk") {
        const promises = pendingPayment.items.map((selected) => {
          const item = filteredData.find(
            (d) => d.id === selected.id && d.type === selected.type
          );
          const amount = Number(item?.amount_to_pay || 0);

          if (selected.type === "mileage") {
            return secureAxios.post(`/api/mileage/mark-paid/${selected.id}`, {
              amount_paid: amount,
              paid_at: paymentDate,
            });
          } else {
            return secureAxios.patch(`/api/job-logs/${selected.id}/mark-paid`, {
              paid_by: user?.user_id,
              amount_paid: amount,
              paid_at: paymentDate,
              purpose_of_use: "payment",
            });
          }
        });

        await Promise.all(promises);
        toast.success(
          `${
            pendingPayment.count
          } items marked as paid for $${pendingPayment.amount.toFixed(2)}`
        );
        setSelectedItems([]);
      }

      setShowPaymentModal(false);
      setPendingPayment(null);
      fetchAllData();
    } catch (err) {
      console.error("Error processing payment");
      toast.error("Failed to process payment");
    }
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      is_paid: "",
      is_billable: "",
    });
    setSearchTerm("");
    setSelectedItems([]);
  };

  if (loadingUser) return null;

  if (!is_admin) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Unauthorized</h4>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mt-4">
        <p>Loading payment data...</p>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <h4>Payment Review</h4>

      {/* HIPAA Compliance Notices */}
      <PHIWarningBanner />
      <PurposeOfUseBanner purpose="Payment Processing for Services & Mileage Reimbursement" />

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "all" ? "active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All ({getNormalizedData().length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "joblogs" ? "active" : ""}`}
            onClick={() => setActiveTab("joblogs")}
          >
            Job Logs ({jobLogs.length})
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "mileage" ? "active" : ""}`}
            onClick={() => setActiveTab("mileage")}
          >
            Mileage ({mileageLogs.length})
          </button>
        </li>
      </ul>

      {/* Filters */}
      <div className="row mb-3">
        <div className="col-md-2">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.start_date}
            onChange={(e) =>
              setFilters({ ...filters, start_date: e.target.value })
            }
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">End Date</label>
          <input
            type="date"
            className="form-control"
            value={filters.end_date}
            onChange={(e) =>
              setFilters({ ...filters, end_date: e.target.value })
            }
          />
        </div>
        <div className="col-md-2">
          <label className="form-label">Status</label>
          <select
            className="form-select"
            value={filters.is_paid}
            onChange={(e) =>
              setFilters({ ...filters, is_paid: e.target.value })
            }
          >
            <option value="">All</option>
            <option value="false">Unpaid Only</option>
            <option value="true">Paid Only</option>
          </select>
        </div>
        <div className="col-md-2">
          <label className="form-label">Billable Status</label>
          <select
            className="form-select"
            value={filters.is_billable}
            onChange={(e) =>
              setFilters({ ...filters, is_billable: e.target.value })
            }
          >
            <option value="">All</option>
            <option value="true">Billable Only</option>
            <option value="false">Non-billable Only</option>
          </select>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <button
            className="btn btn-outline-secondary"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Consumer or Job Coach"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Summary */}
      <div className="alert alert-info mb-3">
        <div className="row">
          <div className="col-md-3">
            <strong>Total Records:</strong> {filteredData.length}
          </div>
          <div className="col-md-3">
            <strong>Total Amount:</strong> ${totalAmount.toFixed(2)}
          </div>
          <div className="col-md-3">
            <strong>Unpaid Amount:</strong> ${unpaidTotal.toFixed(2)}
          </div>
          <div className="col-md-3">
            <strong>Selected Amount:</strong> ${selectedTotal.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Bulk action button */}
      {selectedItems.length > 0 && (
        <div className="mb-3">
          <button className="btn btn-success" onClick={markSelectedAsPaid}>
            Mark {selectedItems.length} Selected as Paid ($
            {selectedTotal.toFixed(2)})
          </button>
        </div>
      )}

      {/* Desktop/tablet view */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-bordered table-striped mt-3">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={
                    selectedItems.length > 0 &&
                    selectedItems.length ===
                      filteredData.filter((d) => !d.is_paid).length
                  }
                />
              </th>
              <th>Type</th>
              <th>Date</th>
              <th>Consumer</th>
              <th>Job Coach</th>
              <th>Authorization</th>
              <th>Billable</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Amount</th>
              <th>Paid?</th>
              <th>Date Paid</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((item) => (
              <tr key={`${item.type}-${item.id}`}>
                <td>
                  {!item.is_paid && (
                    <input
                      type="checkbox"
                      checked={isSelected(item.type, item.id)}
                      onChange={() => handleSelectItem(item.type, item.id)}
                    />
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${
                      item.type === "mileage" ? "bg-info" : "bg-primary"
                    }`}
                  >
                    {item.type === "mileage" ? "Mileage" : "Job Log"}
                  </span>
                </td>
                <td>{formatDate(item.date)}</td>
                <td>{item.consumer_name || "Non-billable"}</td>
                <td>{item.user_name}</td>
                <td>
                  {item.authorization_number || "N/A"}
                  {item.service_name && (
                    <div><small className="text-muted">{item.service_name}</small></div>
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${
                      item.is_billable !== false ? "bg-success" : "bg-secondary"
                    }`}
                  >
                    {item.is_billable !== false ? "Yes" : "No"}
                  </span>
                </td>
                <td>
                  {item.quantity} {item.quantity_label}
                </td>
                <td>${item.rate ? Number(item.rate).toFixed(2) : "0.00"}</td>
                <td>${Number(item.amount_to_pay || 0).toFixed(2)}</td>
                <td>
                  {item.is_paid ? (
                    <span className="text-success">Yes</span>
                  ) : (
                    <span className="text-danger">No</span>
                  )}
                </td>
                <td>
                  {item.is_paid && item.paid_at ? (
                    formatDate(item.paid_at)
                  ) : item.previously_paid_at ? (
                    <span
                      className="text-muted"
                      title="Previously paid on this date"
                    >
                      <small>Prev: {formatDate(item.previously_paid_at)}</small>
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
                <td>
                  {!item.is_paid ? (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => markAsPaid(item.type, item.id)}
                    >
                      Mark as Paid
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={() => markAsUnpaid(item.type, item.id)}
                    >
                      Mark as Unpaid
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile-friendly stacked cards */}
      <ul className="list-group d-block d-md-none mt-3">
        {filteredData.map((item) => (
          <li key={`${item.type}-${item.id}`} className="list-group-item">
            {!item.is_paid && (
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isSelected(item.type, item.id)}
                  onChange={() => handleSelectItem(item.type, item.id)}
                  id={`check-${item.type}-${item.id}`}
                />
                <label
                  className="form-check-label"
                  htmlFor={`check-${item.type}-${item.id}`}
                >
                  Select for bulk payment
                </label>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span
                className={`badge ${
                  item.type === "mileage" ? "bg-info" : "bg-primary"
                }`}
              >
                {item.type === "mileage" ? "Mileage" : "Job Log"}
              </span>
              <span
                className={`badge ${
                  item.is_billable !== false ? "bg-success" : "bg-secondary"
                }`}
              >
                {item.is_billable !== false ? "Billable" : "Non-billable"}
              </span>
            </div>
            <div>
              <strong>Date:</strong> {formatDate(item.date)}
            </div>
            <div>
              <strong>Consumer:</strong> {item.consumer_name || "Non-billable"}
            </div>
            <div>
              <strong>Job Coach:</strong> {item.user_name}
            </div>
            <div>
              <strong>Authorization:</strong>{" "}
              {item.authorization_number || "N/A"}
              {item.service_name && (
                <div><small className="text-muted">{item.service_name}</small></div>
              )}
            </div>
            <div>
              <strong>Quantity:</strong> {item.quantity} {item.quantity_label}
            </div>
            <div>
              <strong>Rate:</strong> $
              {item.rate ? Number(item.rate).toFixed(2) : "0.00"}
            </div>
            <div>
              <strong>Amount to Pay:</strong> $
              {Number(item.amount_to_pay || 0).toFixed(2)}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              {item.is_paid ? (
                <span className="text-success">Paid</span>
              ) : (
                <span className="text-danger">Unpaid</span>
              )}
            </div>
            {item.is_paid && item.paid_at && (
              <div>
                <strong>Date Paid:</strong> {formatDate(item.paid_at)}
              </div>
            )}
            {!item.is_paid && item.previously_paid_at && (
              <div className="text-muted">
                <strong>Previously Paid:</strong>{" "}
                {formatDate(item.previously_paid_at)}
              </div>
            )}

            {!item.is_paid ? (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-success"
                  onClick={() => markAsPaid(item.type, item.id)}
                >
                  Mark as Paid
                </button>
              </div>
            ) : (
              <div className="mt-2">
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => markAsUnpaid(item.type, item.id)}
                >
                  Mark as Unpaid
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Data Handling Notice */}
      <DataHandlingNotice />

      {/* Payment Date Modal */}
      {showPaymentModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Payment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPendingPayment(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  {pendingPayment?.type === "single" ? (
                    <>
                      Mark this{" "}
                      {pendingPayment?.recordType === "mileage"
                        ? "mileage"
                        : "job log"}{" "}
                      as paid for{" "}
                      <strong>${pendingPayment?.amount?.toFixed(2)}</strong>?
                    </>
                  ) : (
                    <>
                      Mark <strong>{pendingPayment?.count}</strong> items as
                      paid for{" "}
                      <strong>${pendingPayment?.amount?.toFixed(2)}</strong>?
                    </>
                  )}
                </p>

                <div className="mb-3">
                  <label htmlFor="paymentDate" className="form-label">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="paymentDate"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                  />
                  <small className="text-muted">
                    Enter the date the payment was or will be made
                  </small>
                </div>

                <div className="alert alert-info small mb-0">
                  <strong>By confirming, you acknowledge that:</strong>
                  <ul className="mb-0 mt-2">
                    <li>This action will be logged for audit purposes</li>
                    <li>You have verified the accuracy of this payment</li>
                    <li>You are authorized to process this payment</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPendingPayment(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={confirmPayment}
                >
                  Confirm Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentReviewPage;